import React, { useEffect, useRef, useState } from 'react';

// MANAGE — the workspace's furniture (tags today), ported whole from
// legacy/app.js (2026-08-23, phase 1 of the React port). Portals into the
// legacy #page-manage container so the .tag-* dress in legacy/style.css
// applies unchanged.
//
// THE CATALOG STAYS THE ENGINE'S (one source of truth): the outliner's chips
// resolve ids through app.js's _tags, so every write here refreshes THAT
// cache (window.loadTags(true)) and repaints whatever wears tags
// (window.redrawTagsEverywhere), exactly as the legacy page did.

const TAG_COLORS = [
  { key: 'sand', label: 'sand' }, { key: 'clay', label: 'clay' },
  { key: 'rust', label: 'rust' }, { key: 'rose', label: 'rose' },
  { key: 'gold', label: 'gold' }, { key: 'moss', label: 'moss' },
  { key: 'sage', label: 'sage' }, { key: 'teal', label: 'teal' },
  { key: 'denim', label: 'denim' }, { key: 'indigo', label: 'indigo' },
  { key: 'plum', label: 'plum' }, { key: 'slate', label: 'slate' },
];
const TAG_SWATCH_VISIBLE = 6;

// The six-then-reveal swatch picker (the legacy makeSwatchRow, as a component):
// six covers the common case, "···" opens the wheel, and the selected colour
// is always among the visible six so a row never renders with nothing marked.
function SwatchRow({ selected, onPick, className }) {
  const [expanded, setExpanded] = useState(false);
  let visible;
  if (expanded) visible = TAG_COLORS;
  else {
    const head = TAG_COLORS.slice(0, TAG_SWATCH_VISIBLE);
    if (head.some((c) => c.key === selected)) visible = head;
    else {
      const sel = TAG_COLORS.find((c) => c.key === selected);
      visible = sel ? [...head.slice(0, TAG_SWATCH_VISIBLE - 1), sel] : head;
    }
  }
  return (
    <div className={`tag-swatches${className ? ` ${className}` : ''}`}>
      {visible.map((c) => (
        <button key={c.key} type="button"
                className={`tag-swatch${c.key === selected ? ' on' : ''}`}
                data-color={c.key} title={c.label} aria-label={c.label}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPick(c.key); }} />
      ))}
      <button type="button" className={`tag-swatch-more${expanded ? ' on' : ''}`}
              title={expanded ? 'Fewer colours' : 'More colours'}
              aria-expanded={expanded}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded((v) => !v); }}>
        {expanded ? '‹' : '···'}
      </button>
    </div>
  );
}

// One row: the chip (click to rename in place), a colour picker, delete.
function TagRow({ tag, onChanged }) {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(tag.label);
  const doneRef = useRef(false);

  const commit = async () => {
    if (doneRef.current) return;
    doneRef.current = true;
    const label = draft.trim();
    setRenaming(false);
    if (!label || label === tag.label) return;
    try {
      await window.putJson(`/api/tags/${tag.id}`, { label });
      // Items hold the id and resolve through the catalog — refresh it and
      // repaint; nothing on the items themselves needs rewriting.
      window.invalidateMissionCache?.();
      await window.loadTags?.(true);
      window.redrawTagsEverywhere?.();
      onChanged();
    } catch (ex) {
      alert(`Couldn't rename — ${ex.message}`);
    }
  };

  return (
    <div className="tag-row">
      <div className="tag-row-chip">
        {renaming ? (
          <input type="text" className="tag-rename-input" value={draft} maxLength={32} spellCheck={false}
                 autoFocus
                 onFocus={(e) => e.target.select()}
                 onChange={(e) => setDraft(e.target.value)}
                 onKeyDown={(e) => {
                   if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
                   if (e.key === 'Escape') { e.preventDefault(); doneRef.current = true; setRenaming(false); }
                 }}
                 onBlur={commit} />
        ) : (
          <span className="tag-chip clickable" data-color={tag.color || 'sand'} title="Click to rename"
                onClick={() => { doneRef.current = false; setDraft(tag.label); setRenaming(true); }}>
            <span className="tag-chip-label">{tag.label}</span>
          </span>
        )}
      </div>
      <SwatchRow className="tag-row-swatches" selected={tag.color || 'sand'}
                 onPick={async (key) => {
                   try {
                     await window.putJson(`/api/tags/${tag.id}`, { color: key });
                     window.invalidateMissionCache?.();
                     await window.loadTags?.(true);
                     window.redrawTagsEverywhere?.();
                     onChanged();
                   } catch (ex) {
                     alert(`Couldn't recolour — ${ex.message}`);
                   }
                 }} />
      <button type="button" className="tag-row-del" title="Delete this tag"
              onClick={async () => {
                if (!confirm(`Delete "${tag.label}"? It comes off everything wearing it.`)) return;
                try {
                  await window.fetchJson(`/api/tags/${tag.id}`, { method: 'DELETE' });
                  window.invalidateMissionCache?.();
                  await window.loadTags?.(true);
                  window.redrawTagsEverywhere?.();
                  onChanged();
                } catch (ex) {
                  alert(`Couldn't delete — ${ex.message}`);
                }
              }}>
        delete
      </button>
    </div>
  );
}

export default function Manage({ active }) {
  const [tags, setTags] = useState(null);
  const [error, setError] = useState('');
  const [label, setLabel] = useState('');
  const [color, setColor] = useState(TAG_COLORS[0].key);
  const [busy, setBusy] = useState(false);
  const [epoch, setEpoch] = useState(0);

  useEffect(() => {
    if (!active) return undefined;
    let live = true;
    // through the ENGINE's own catalog loader, so its cache stays the truth
    Promise.resolve(window.loadTags?.(true)).then((list) => {
      if (live) setTags([...(list || [])]);
    });
    return () => { live = false; };
  }, [active, epoch]);

  const add = async (e) => {
    e.preventDefault();
    const name = label.trim();
    setError('');
    if (!name) return;
    setBusy(true);
    try {
      await window.postJson('/api/tags', { label: name, color });
      setLabel('');
      setEpoch((n) => n + 1);
    } catch (ex) {
      setError(ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="manage-section">
      <div className="manage-section-head">
        <h2 className="manage-section-title">Tags</h2>
        <p className="manage-section-sub">Labels you can put on any goal or action, in any mission. Create one here, then add it from the item's details.</p>
      </div>

      <form className="tag-new" onSubmit={add}>
        <input type="text" className="tag-new-input" placeholder="New tag…" maxLength={32} spellCheck={false}
               value={label} onChange={(e) => setLabel(e.target.value)} />
        <SwatchRow selected={color} onPick={setColor} />
        <button type="submit" className="tag-new-btn" disabled={busy}>Add tag</button>
      </form>
      <div className="tag-new-error">{error}</div>

      {tags === null && <div className="loading">loading…</div>}
      {tags !== null && (tags.length
        ? <div className="tag-list">{tags.map((t) => <TagRow key={t.id} tag={t} onChanged={() => setEpoch((n) => n + 1)} />)}</div>
        : <div className="tag-empty">No tags yet — name your first one above.</div>)}
    </section>
  );
}
